import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ServiceRequestFormData, Location, LocationType, OrganizationData, Coordinates } from '../../types';
import { supabase } from '../../config/supabaseClient';
import { canAccessFeature } from '../../utils/tierPermissions';
import './StepStyles.scss';
import {
  GoogleMap,
  useLoadScript,
  Libraries,
  DirectionsService,
  DirectionsRenderer,
  Marker,
  InfoWindow
} from '@react-google-maps/api';

const mapLibraries: Libraries = ['places', 'geometry'];
const METERS_TO_MILES = 0.000621371;

interface LocationInput {
  id: number;
  address: string;
  city: string;
  state: string;
  zip_code: string;
}

interface LocationDistanceResult {
  locationId: number;
  status: string;
  distanceText?: string;
  distanceValue?: number;
  durationText?: string;
  durationValue?: number;
  googleOriginAddress?: string;
  googleDestinationAddress?: string;
  originCoordinates?: Coordinates | null;
  destinationCoordinates?: Coordinates | null;
}

interface Step3LocationProps {
  formData: ServiceRequestFormData;
  updateFormData: (data: Partial<ServiceRequestFormData>) => void;
  registerValidation: (validator: () => boolean) => void;
  organizationData: OrganizationData;
  variant?: 'form' | 'qr';
}

const defaultMapCenter = { lat: 39.8283, lng: -98.5795 }; // Center of US

function Step3Location({
  formData,
  updateFormData,
  registerValidation,
  organizationData,
  variant = 'form',
}: Step3LocationProps) {
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [locationsWithDistance, setLocationsWithDistance] = useState<Location[]>([]);
  const [suggestedLocationId, setSuggestedLocationId] = useState<number | null>(null);
  const [customerCoordinates, setCustomerCoordinates] = useState<Coordinates | null>(null);
  const [mapCenter, setMapCenter] = useState<Coordinates>(defaultMapCenter);
  const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);
  const [directionsError, setDirectionsError] = useState<string | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const currentDirectionsRequest = useRef<{ origin: Coordinates; destination: Coordinates } | null>(null);
  const [activeMarkerId, setActiveMarkerId] = useState<string | null>(null);

  // Simple dropdown is used when the org's tier doesn't get the map view OR no customer address is available (QR flow).
  const hasCustomerAddress = !!(formData.address && formData.city && formData.state && formData.zipCode);
  const useSimpleLocationPicker = !canAccessFeature(organizationData?.organization?.tier, 'mapBasedLocation') || !hasCustomerAddress;

  const MAP_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_JS_API_KEY;
  if (!MAP_API_KEY && !useSimpleLocationPicker) {
    console.error("VITE_GOOGLE_MAPS_JS_API_KEY is not set!");
  }

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: !useSimpleLocationPicker && MAP_API_KEY ? MAP_API_KEY : "",
    libraries: mapLibraries,
  });

  const customerAddressString = useMemo(() => {
    if (formData.address && formData.city && formData.state && formData.zipCode) {
      return `${formData.address}, ${formData.city}, ${formData.state} ${formData.zipCode}`;
    }
    return null;
  }, [formData.address, formData.city, formData.state, formData.zipCode]);

  useEffect(() => {
    setCustomerCoordinates(null);
    setDirections(null);
    setDirectionsError(null);
    setMapCenter(defaultMapCenter);
    setActiveMarkerId(null);
    currentDirectionsRequest.current = null;

    // For free tier, skip distance calculation and just show locations
    if (useSimpleLocationPicker) {
      setLocationsWithDistance(organizationData.locations.map(loc => ({...loc, coordinates: undefined})));
      setIsDataLoading(false);
      setError(null);
      return;
    }

    if (!isLoaded || !customerAddressString || !organizationData?.locations?.length) {
      setIsDataLoading(false);
      if (!customerAddressString && organizationData?.locations?.length > 0) {
        if (variant === 'form') setError("Please complete your address information in the previous step.");
      } else {
        setError(null);
      }
      setLocationsWithDistance(organizationData.locations.map(loc => ({...loc, coordinates: undefined})));
      return;
    }

    let isMounted = true;
    const fetchDistances = async () => {
      setIsDataLoading(true);
      setError(null);
      setSuggestedLocationId(null);

      const locationInputs: LocationInput[] = organizationData.locations.map(loc => ({
        id: loc.id,
        address: loc.address,
        city: loc.city,
        state: loc.state,
        zip_code: loc.zip_code,
      }));

      try {
        const { data, error: functionError } = await supabase.functions.invoke<LocationDistanceResult[]>('calculate-distances', {
          body: { customerAddress: customerAddressString, locations: locationInputs },
        });

        if (!isMounted) return;
        if (functionError) throw functionError;
        if (!data) throw new Error('No data returned from distance calculation function.');

        let closestLocationId: number | null = null;
        let minDistance = Infinity;
        let foundOriginCoords: Coordinates | null = null;

        const updatedLocations = organizationData.locations.map(loc => {
          const result = data.find(d => d.locationId === loc.id);
          if (!result) return { ...loc, coordinates: null };

          if (!foundOriginCoords && result.originCoordinates) {
            foundOriginCoords = result.originCoordinates;
          }

          if (result.status === 'OK' && typeof result.distanceValue === 'number') {
            if (result.distanceValue < minDistance) {
              minDistance = result.distanceValue;
              closestLocationId = loc.id;
            }
            return {
              ...loc,
              distanceText: result.distanceText,
              distanceValue: result.distanceValue,
              durationText: result.durationText,
              durationValue: result.durationValue,
              coordinates: result.destinationCoordinates ?? null,
            };
          } else {
            return { ...loc, coordinates: result.destinationCoordinates ?? null };
          }
        });

        if (!isMounted) return;

        setLocationsWithDistance(updatedLocations);
        setSuggestedLocationId(closestLocationId);
        setCustomerCoordinates(foundOriginCoords);

        if (foundOriginCoords) {
          setMapCenter(foundOriginCoords);
        } else {
          setMapCenter(defaultMapCenter);
          setError(prev => prev ? `${prev} Could not determine your location coordinates.` : "Could not determine your location coordinates.");
        }

        if (closestLocationId !== null && minDistance !== Infinity) {
          if (formData.selectedLocationId !== closestLocationId) {
            updateFormData({
              selectedLocationId: closestLocationId,
              distanceToSelectedLocation: minDistance * METERS_TO_MILES, // Convert meters to miles
            });
          }
        } else if (!closestLocationId && organizationData.locations.length > 0) {
          setError("Could not calculate driving distances. Please select a location manually.");
          if (formData.selectedLocationId !== null) {
            updateFormData({
              selectedLocationId: null,
              distanceToSelectedLocation: null,
            });
          }
        }

      } catch (err: any) {
        if (!isMounted) return;
        setError(`Failed to calculate distances: ${err.message || 'Unknown error'}. Please select a location manually.`);
        setLocationsWithDistance(organizationData.locations.map(loc => ({...loc, coordinates: undefined})));
        if (formData.selectedLocationId !== null) {
          updateFormData({
            selectedLocationId: null,
            distanceToSelectedLocation: null,
          });
        }
      } finally {
        if (isMounted) setIsDataLoading(false);
      }
    };

    fetchDistances();

    return () => { isMounted = false; };
  }, [customerAddressString, organizationData.locations, updateFormData, isLoaded, useSimpleLocationPicker, variant]);

  const directionsCallback = useCallback((
    result: google.maps.DirectionsResult | null,
    status: google.maps.DirectionsStatus
  ) => {
    if (status === google.maps.DirectionsStatus.OK && result) {
      setDirections(prevDirections => {
        if (prevDirections === null) {
          if (mapRef.current && result.routes?.[0]?.bounds) {
            mapRef.current.fitBounds(result.routes[0].bounds);
          }
          return result;
        }
        return prevDirections;
      });
      setDirectionsError(null);
    } else if (status !== google.maps.DirectionsStatus.OK) {
      setDirections(null);
      setDirectionsError(`Could not calculate directions: ${status}`);
    }
  }, []);

  const validateStep = useCallback(() => {
    const isValid = formData.selectedLocationId != null && formData.selectedLocationId > 0;
    if (!isValid) {
      setError("Please select a service location.");
      return false;
    }
    setError(null);
    return true;
  }, [formData.selectedLocationId]);

  useEffect(() => {
    registerValidation(validateStep);
  }, [registerValidation, validateStep]);

  const handleLocationChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const selectedId = parseInt(event.target.value, 10);
    const selectedLocation = locationsWithDistance.find(loc => loc.id === selectedId);

    if (selectedLocation) {
      updateFormData({
        selectedLocationId: selectedId,
        distanceToSelectedLocation: selectedLocation.distanceValue ? selectedLocation.distanceValue * METERS_TO_MILES : null, // Convert meters to miles
      });
      setError(null);
      setDirections(null);
      setDirectionsError(null);
      currentDirectionsRequest.current = null;

      if (isLoaded && mapRef.current) {
        if (customerCoordinates && selectedLocation.coordinates) {
          const bounds = new google.maps.LatLngBounds();
          bounds.extend(customerCoordinates);
          bounds.extend(selectedLocation.coordinates);
          mapRef.current.fitBounds(bounds);
        } else if (selectedLocation.coordinates) {
          setMapCenter(selectedLocation.coordinates);
        }
      } else if (selectedLocation.coordinates) {
        setMapCenter(selectedLocation.coordinates);
      }
    }
  };

  const handleSuggestedLocationSelect = () => {
    if (suggestedLocationId) {
      const selectedLocation = locationsWithDistance.find(loc => loc.id === suggestedLocationId);
      if (selectedLocation) {
        updateFormData({
          selectedLocationId: suggestedLocationId,
          distanceToSelectedLocation: selectedLocation.distanceValue ? selectedLocation.distanceValue * METERS_TO_MILES : null,
        });
        setError(null);
      }
    }
  };

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    
    const selectedLocation = locationsWithDistance.find(l => l.id === formData.selectedLocationId);
    if (customerCoordinates && selectedLocation?.coordinates && !directions && mapRef.current) {
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(customerCoordinates);
      bounds.extend(selectedLocation.coordinates);
      mapRef.current.fitBounds(bounds);
    }
  }, [directions, customerCoordinates, formData.selectedLocationId, locationsWithDistance]);

  const onMapUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  const handleMarkerClick = useCallback((markerId: string) => { 
    setActiveMarkerId(prevId => prevId === markerId ? null : markerId); 
  }, []);

  const handleInfoWindowClose = useCallback(() => { 
    setActiveMarkerId(null); 
  }, []);

  const selectedLocationDetails = locationsWithDistance.find(l => l.id === formData.selectedLocationId);
  
  const shouldRequestAndRenderDirections =
    isLoaded &&
    customerCoordinates &&
    selectedLocationDetails?.coordinates &&
    directions === null;

  if (shouldRequestAndRenderDirections && selectedLocationDetails?.coordinates) {
    currentDirectionsRequest.current = {
      origin: customerCoordinates,
      destination: selectedLocationDetails.coordinates,
    };
  }

  const customerIcon = useMemo(() => {
    if (!isLoaded || !window.google?.maps?.SymbolPath) return undefined;
    return {
      path: google.maps.SymbolPath.CIRCLE,
      fillColor: '#4285F4',
      fillOpacity: 1.0,
      strokeColor: '#ffffff',
      strokeWeight: 2,
      scale: 10,
    };
  }, [isLoaded]);

  const locationIcon = useMemo(() => {
    if (!isLoaded || !window.google?.maps?.SymbolPath) return undefined;
    return {
      path: google.maps.SymbolPath.CIRCLE,
      fillColor: '#34A853',
      fillOpacity: 1.0,
      strokeColor: '#ffffff',
      strokeWeight: 1.5,
      scale: 8,
      labelOrigin: new google.maps.Point(0, 0.5)
    };
  }, [isLoaded]);

  const formatLocationTypeDisplay = (type: LocationType | null | undefined): string => {
      if (!type) return '';
      switch(type) {
          case 'ag': return ' (AG)';
          case 'lawn_garden': return ' (Lawn & Garden)';
          case 'combo': return ' (Combo)';
          default: return '';
      }
  };

  const markerLabelStyle = useMemo(() => ({
    className: 'map-marker-label',
    text: '',
    color: '',
    fontFamily: '',
    fontSize: '',
    fontWeight: '',
  }), []);

  const mapOptions = useMemo(() => ({
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: true,
    zoomControl: true,
    styles: [
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }]
      },
      {
        featureType: "transit",
        elementType: "labels.icon",
        stylers: [{ visibility: "off" }]
      }
    ]
  }), []);

  const directionsRendererOptions = useMemo(() => ({
    suppressMarkers: true,
    polylineOptions: {
      strokeColor: '#4285F4',
      strokeOpacity: 0.8,
      strokeWeight: 5
    }
  }), []);

  if (loadError) {
    return (
      <div className="form-step step-location">
        <h2>Select Service Location</h2>
        <div className="error-message summary-error">
          Map loading failed. Please select a location from the list.
        </div>

        {!isDataLoading && locationsWithDistance.length > 0 && (
          <div className="location-selection-area">
            {suggestedLocationId && !error && (
              <p className="step-intro">
                Based on your address, the closest location is suggested below. You can select a different one if needed.
              </p>
            )}
            {!suggestedLocationId && !error && customerAddressString && locationsWithDistance.some(l => l.distanceValue === undefined) && (
              <p className="step-intro">
                We couldn't automatically determine the closest location. Please choose from the list below.
              </p>
            )}
            {!suggestedLocationId && !error && !customerAddressString && (
              <p className="step-intro">
                Please select your preferred service location from the list below.
              </p>
            )}

            <div className="radio-group location-options">
              {locationsWithDistance.map((location) => (
                <div
                  key={location.id}
                  className={`radio-option location-option ${location.id === suggestedLocationId ? 'suggested' : ''} ${location.id === formData.selectedLocationId ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    id={`location-${location.id}`}
                    name="selectedLocationId"
                    value={location.id}
                    checked={formData.selectedLocationId === location.id}
                    onChange={handleLocationChange}
                    disabled={isDataLoading}
                  />
                  <label htmlFor={`location-${location.id}`}>
                    <span className="location-name">{location.name}</span>
                    <span className="location-address">
                      {location.address}, {location.city}, {location.state} {location.zip_code}
                    </span>
                    {location.distanceText && location.durationText && (
                      <span className="location-distance">
                        ~ {location.distanceText} ({location.durationText} drive)
                        {location.id === suggestedLocationId ? <span className="suggested-badge"> Closest</span> : ""}
                      </span>
                    )}
                    {customerAddressString && location.distanceValue === undefined && !isDataLoading && (
                      <span className="location-distance-error"> (Could not calculate distance)</span>
                    )}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {!isDataLoading && !organizationData.locations?.length && (
          <p>No service locations are currently configured for this organization.</p>
        )}
      </div>
    );
  }

  const showScriptLoading = !isLoaded && !loadError;
  const showDataLoadingIndicator = isDataLoading && isLoaded;

  return (
    <div className="form-step step-location">
      <h2>{variant === 'qr' ? 'Choose a branch' : 'Select Service Location'}</h2>

      {variant === 'form' && !customerAddressString && organizationData.locations.length > 0 && (
        <div className="error-message summary-error">
          Please complete your address in the previous step to find the nearest location.
        </div>
      )}

      {showDataLoadingIndicator && <div className="loading-indicator">Calculating distances...</div>}

      {error && !isDataLoading && (
        <div className="error-message summary-error">{error}</div>
      )}

      {!isDataLoading && locationsWithDistance.length > 0 && (
        <div className="location-selection-area">
          {useSimpleLocationPicker ? (
            /* Free Tier: Simple Dropdown */
            <div className="form-group">
              <label htmlFor="locationSelect" className="required">{variant === 'qr' ? 'Which branch should handle this?' : 'Select Service Location'}</label>
              <select
                id="locationSelect"
                name="selectedLocationId"
                value={formData.selectedLocationId || ''}
                onChange={handleLocationChange}
                required
              >
                <option value="">Choose a location...</option>
                {[...locationsWithDistance]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}{formatLocationTypeDisplay(location.location_type)} - {location.city}, {location.state}
                    </option>
                  ))}
              </select>
            </div>
          ) : (
            /* Basic+ Tier: Suggested Card + Dropdown */
            <>
              {suggestedLocationId && !error && customerAddressString && (
                <div className="suggested-location-card">
                  <div className="suggested-badge-header">Closest Location</div>
                  {(() => {
                    const suggested = locationsWithDistance.find(l => l.id === suggestedLocationId);
                    if (!suggested) return null;
                    return (
                      <>
                        <h4 className="location-name">
                          {suggested.name}{formatLocationTypeDisplay(suggested.location_type)}
                        </h4>
                        <p className="location-address">
                          {suggested.address}, {suggested.city}, {suggested.state} {suggested.zip_code}
                        </p>
                        {suggested.distanceText && suggested.durationText && (
                          <p className="location-distance">
                            <strong>{suggested.distanceText}</strong> away ({suggested.durationText} drive)
                          </p>
                        )}
                        <button
                          type="button"
                          className={`btn ${formData.selectedLocationId === suggestedLocationId ? 'btn-success' : 'btn-primary'}`}
                          onClick={handleSuggestedLocationSelect}
                        >
                          {formData.selectedLocationId === suggestedLocationId ? 'Selected ✓' : 'Select This Location'}
                        </button>
                      </>
                    );
                  })()}
                </div>
              )}

              <div className="alternative-locations-section">
                <label htmlFor="alternativeLocationSelect" className={suggestedLocationId ? '' : 'required'}>
                  {suggestedLocationId ? 'Or choose a different location' : 'Select Service Location'}
                </label>
                <select
                  id="alternativeLocationSelect"
                  name="selectedLocationId"
                  value={formData.selectedLocationId || ''}
                  onChange={handleLocationChange}
                  required={!suggestedLocationId}
                >
                  <option value="">Choose a location...</option>
                  {[...locationsWithDistance]
                    .sort((a, b) => {
                      if (a.distanceValue === undefined && b.distanceValue === undefined) return a.name.localeCompare(b.name);
                      if (a.distanceValue === undefined) return 1;
                      if (b.distanceValue === undefined) return -1;
                      return a.distanceValue - b.distanceValue;
                    })
                    .map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}{formatLocationTypeDisplay(location.location_type)} - {location.city}, {location.state}
                        {location.distanceText ? ` (${location.distanceText})` : ''}
                      </option>
                    ))}
                </select>
              </div>
            </>
          )}
        </div>
      )}

      {!isDataLoading && !organizationData.locations?.length && (
        <p>No service locations are currently configured for this organization.</p>
      )}

      {MAP_API_KEY && !useSimpleLocationPicker && (
        <div className="location-map-container" style={{ marginTop: 'var(--spacing-xl)' }}>
          {selectedLocationDetails ? <h3>Route to {selectedLocationDetails.name}</h3> : <h3>Service Location Map</h3>}

          {showScriptLoading && (
            <div className="map-placeholder" style={{ 
              height: '400px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              background: '#f0f0f0', 
              borderRadius: 'var(--border-radius)',
              marginTop: 'var(--spacing-md)'
            }}>
              <div>Loading map...</div>
            </div>
          )}

          {isLoaded && (
            <GoogleMap
              mapContainerStyle={{ 
                height: '400px', 
                width: '100%', 
                marginTop: 'var(--spacing-md)', 
                borderRadius: 'var(--border-radius)' 
              }}
              center={mapCenter}
              zoom={mapRef.current?.getZoom() ?? 10}
              onLoad={onMapLoad}
              onUnmount={onMapUnmount}
              options={mapOptions}
              onClick={handleInfoWindowClose}
            >
              <>
                {shouldRequestAndRenderDirections && (
                  <DirectionsService
                    key={`${customerCoordinates?.lat}-${selectedLocationDetails?.coordinates?.lat}`}
                    options={{
                      destination: { lat: selectedLocationDetails!.coordinates!.lat, lng: selectedLocationDetails!.coordinates!.lng },
                      origin: { lat: customerCoordinates!.lat, lng: customerCoordinates!.lng },
                      travelMode: google.maps.TravelMode.DRIVING
                    }}
                    callback={directionsCallback}
                  />
                )}

                {directions && (
                  <DirectionsRenderer
                    options={{
                      ...directionsRendererOptions,
                      directions: directions
                    }}
                  />
                )}

                {customerCoordinates && customerIcon && (
                  <Marker
                    position={customerCoordinates}
                    title={"Your Location"}
                    icon={customerIcon}
                    label={{ ...markerLabelStyle, text: "You" }}
                    onClick={() => handleMarkerClick('customer')}
                    zIndex={1}
                  />
                )}
                
                {selectedLocationDetails?.coordinates && locationIcon && (
                  <Marker
                    position={selectedLocationDetails.coordinates}
                    title={selectedLocationDetails.name}
                    icon={locationIcon}
                    label={{ ...markerLabelStyle, text: "Service Location" }}
                    onClick={() => handleMarkerClick(`location-${selectedLocationDetails.id}`)}
                    zIndex={2}
                  />
                )}

                {selectedLocationDetails?.coordinates && activeMarkerId === `location-${selectedLocationDetails.id}` && (
                  <InfoWindow
                    position={selectedLocationDetails.coordinates}
                    onCloseClick={handleInfoWindowClose}
                    options={{
                      pixelOffset: new google.maps.Size(0, -38),
                    }}
                  >
                    <div className="map-infowindow-content">
                      <h4>{selectedLocationDetails.name}</h4>
                      <p>{selectedLocationDetails.address}</p>
                      {selectedLocationDetails.distanceText && (
                        <p className="distance-info">
                          ~ {selectedLocationDetails.distanceText} drive
                        </p>
                      )}
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&origin=${customerCoordinates?.lat},${customerCoordinates?.lng}&destination=${selectedLocationDetails.coordinates.lat},${selectedLocationDetails.coordinates.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="infowindow-directions-link"
                      >
                        Open Directions
                      </a>
                    </div>
                  </InfoWindow>
                )}

                {customerCoordinates && activeMarkerId === 'customer' && (
                  <InfoWindow
                    position={customerCoordinates}
                    onCloseClick={handleInfoWindowClose}
                    options={{ pixelOffset: new google.maps.Size(0, -38) }}
                  >
                    <div className="map-infowindow-content">
                      <h4>Your Approximate Location</h4>
                      {customerAddressString && <p>{customerAddressString}</p>}
                    </div>
                  </InfoWindow>
                )}
              </>
            </GoogleMap>
          )}

          {directionsError && (
            <div className="error-message summary-error map-directions-error" style={{marginTop: 'var(--spacing-sm)'}}>
              {directionsError}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Step3Location;